import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    Container,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Typography,
    Divider,
    Box,
    Badge,
    Paper,
    Alert,
    AlertTitle
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { UserAvatar } from '../components/common/UserAvatar';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { keyframes } from '@mui/system';
import { getUser } from '../services/api';

const DEFAULT_AVATAR = process.env.PUBLIC_URL + '/images/default-avatar.jpg';

// アニメーションの定義
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

function Messages() {
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const { currentUser, blockedUsers } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!currentUser) return;

        // Firestoreからチャットルーム一覧を取得
        const chatRoomsRef = collection(db, 'chatRooms');
        const q = query(
            chatRoomsRef,
            where('participants', 'array-contains', currentUser.uid),
            orderBy('updatedAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, async (snapshot) => {
            try {
                const conversationsData = await Promise.all(
                    snapshot.docs.map(async (doc) => {
                        const data = doc.data();
                        // 相手のユーザーIDを取得
                        const otherUserId = data.participants.find(
                            id => id !== currentUser.uid
                        );

                        // ブロックしているユーザーの場合はスキップ
                        if (blockedUsers.includes(otherUserId)) {
                            return null;
                        }

                        // キャッシュ機能付きでユーザー情報を取得
                        const userData = await getUser(otherUserId);

                        return {
                            id: doc.id,
                            lastMessage: data.lastMessage,
                            lastMessageTime: data.lastMessageTime?.toDate(),
                            otherUser: userData,
                            lastMessageSenderId: data.lastMessageSenderId,
                            locationInfo: data.locationInfo,
                            unreadCount: data.unreadCounts?.[currentUser.uid] || 0,
                        };
                    })
                );

                // nullをフィルタリングして、ブロックしたユーザーを除外
                setConversations(conversationsData.filter(conv => conv !== null));
            } catch (error) {
                console.error('会話の取得に失敗:', error);
            } finally {
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, [currentUser, blockedUsers]);

    if (loading) {
        return <div>読み込み中...</div>;
    }

    // date-fnsの代わりにIntl.DateTimeFormatを使用
    const formatDate = (date) => {
        if (!date) return '';
        return new Intl.DateTimeFormat('ja-JP', {
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    // 会話一覧の表示を拡張
    const ConversationItem = ({ conversation }) => {
        const { currentUser } = useAuth();
        const isOwnMessage = conversation.lastMessageSenderId === currentUser.uid;
        const unreadCount = conversation.unreadCount || 0;

        // メッセージを制限する関数
        const truncateMessage = (message, maxLength = 30) => {
            if (!message) return '';
            return message.length > maxLength
                ? `${message.substring(0, maxLength)}...`
                : message;
        };

        // ニックネームを安全に取得する関数
        const getNickname = () => {
            return conversation?.otherUser?.profile?.nickname || '不明なユーザー';
        };

        // ユーザーが有効かどうかをチェック
        const isValidUser = conversation?.otherUser?.profile?.nickname;

        // プロフィールページへの遷移を処理する関数
        const handleAvatarClick = (e) => {
            e.preventDefault(); // メッセージ一覧への遷移を防止
            if (isValidUser) {
                navigate(`/users/${conversation.otherUser._id}`);
            }
        };

        return (
            <ListItem
                component={isValidUser ? Link : 'div'}
                to={isValidUser ? `/messages/${conversation.id}` : undefined}
                sx={{
                    textDecoration: 'none',
                    color: isValidUser ? 'inherit' : 'text.disabled',
                    backgroundColor: isValidUser ? 'inherit' : 'action.disabledBackground',
                    '&:hover': {
                        backgroundColor: isValidUser
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'action.disabledBackground',
                        cursor: isValidUser ? 'pointer' : 'not-allowed'
                    },
                    px: { xs: 1, sm: 2 },
                    py: { xs: 1.5, sm: 2 }
                }}
            >
                <ListItemAvatar>
                    <Box
                        onClick={handleAvatarClick}
                        sx={{
                            cursor: isValidUser ? 'pointer' : 'not-allowed',
                            display: 'inline-block'
                        }}
                    >
                        <UserAvatar user={conversation.otherUser} />
                    </Box>
                </ListItemAvatar>
                <ListItemText
                    primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
                                <Typography
                                    component="span"
                                    sx={{
                                        mr: 1,
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        fontWeight: unreadCount > 0 ? 'bold' : 'normal'
                                    }}
                                >
                                    {getNickname()}
                                </Typography>
                                {conversation?.locationInfo && (
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        color="textSecondary"
                                        sx={{
                                            display: { xs: 'block', sm: 'inline' },
                                            width: { xs: '100%', sm: 'auto' },
                                            mt: { xs: 0.5, sm: 0 }
                                        }}
                                    >
                                        ({conversation.locationInfo.city}の物件)
                                    </Typography>
                                )}
                            </Box>
                            {unreadCount > 0 && (
                                <Badge
                                    badgeContent={unreadCount}
                                    color="primary"
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            fontSize: '0.8rem',
                                            height: '20px',
                                            minWidth: '20px'
                                        }
                                    }}
                                />
                            )}
                        </Box>
                    }
                    secondary={
                        <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    color: isValidUser ? 'text.secondary' : 'text.disabled',
                                    flex: 1,
                                    mr: 1
                                }}
                            >
                                {isOwnMessage ? '自分: ' : `${getNickname()}: `}
                                {truncateMessage(conversation.lastMessage)}
                                {!isValidUser && (
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        sx={{ ml: 1, color: 'error.main' }}
                                    >
                                        (ブロック中または削除済み)
                                    </Typography>
                                )}
                            </Typography>
                            <Typography
                                variant="caption"
                                color={isValidUser ? 'textSecondary' : 'text.disabled'}
                                component="span"
                                sx={{ flexShrink: 0 }}
                            >
                                {formatDate(conversation.lastMessageTime)}
                            </Typography>
                        </Box>
                    }
                />
            </ListItem>
        );
    };

    return (
        <Box sx={{ pb: 7 }}>
            <Alert
                severity="warning"
                variant="outlined"
                icon={<WarningAmberIcon fontSize="inherit" />}
                sx={{
                    mb: 2,
                    mx: { xs: 1, sm: 2 },
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                        width: '100%'
                    }
                }}
            >
                <AlertTitle sx={{ fontWeight: 'bold' }}>安全にご利用いただくために</AlertTitle>
                <Typography variant="body2" component="div">
                    • 個人情報の共有には十分ご注意ください
                    <br />
                    • 顔合わせは周りに人がいる安全な場所で行ってください
                    <br />
                    • 前払い金や保証金要求にはご注意ください
                    <br />
                    • 犯罪行為や詐欺への勧誘があった場合は、すぐに通報してください
                    <br />
                    • やり取り/取引はすべて自己責任となります
                </Typography>
            </Alert>
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {conversations.length > 0 ? (
                    conversations.map((conversation, index) => (
                        <React.Fragment key={conversation.id}>
                            <Paper
                                elevation={0}
                                sx={{
                                    mx: { xs: 1, sm: 2 },
                                    mb: { xs: 1, sm: 2 },
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': {
                                        boxShadow: 2,
                                        borderColor: 'primary.main',
                                        transition: 'all 0.2s ease-in-out'
                                    }
                                }}
                            >
                                <ConversationItem conversation={conversation} />
                            </Paper>
                        </React.Fragment>
                    ))
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '60vh',
                            textAlign: 'center',
                            px: 2
                        }}
                    >
                        <Box
                            sx={{
                                animation: `${floatAnimation} 3s ease-in-out infinite`,
                                mb: 3
                            }}
                        >
                            <ChatBubbleOutlineIcon
                                sx={{
                                    fontSize: '4rem',
                                    color: 'primary.main',
                                    opacity: 0.8
                                }}
                            />
                        </Box>
                        <Typography
                            variant="h5"
                            component="h2"
                            sx={{
                                fontWeight: 'medium',
                                mb: 2,
                                color: 'primary.main'
                            }}
                        >
                            メッセージはまだありません
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ maxWidth: '400px', mx: 'auto' }}
                        >
                            気になる物件のオーナーやルームメイトとメッセージを交換して、
                            理想のお部屋探しを始めましょう。
                        </Typography>
                    </Box>
                )}
            </List>
        </Box>
    );
}

export default Messages; 